import type { UserProfileDocType } from '../db/schemas';

export type PermissionKey =
  | 'transactions:read'
  | 'transactions:create'
  | 'transactions:update'
  | 'transactions:delete'
  | 'reports:view'
  | 'rates:sync'
  | 'rates:manual_override'
  | 'clients:read'
  | 'clients:manage'
  | 'staff:read'
  | 'staff:manage'
  | 'users:manage'
  | 'settings:manage';

export interface PermissionDefinition {
  key: PermissionKey;
  label: string;
  category: 'Finanzas y Transacciones' | 'Reportes' | 'Tasas de Cambio' | 'Clientes y Personal' | 'Administración';
  description: string;
}

export const ALL_PERMISSIONS: PermissionDefinition[] = [
  {
    key: 'transactions:read',
    label: 'Ver Transacciones',
    category: 'Finanzas y Transacciones',
    description: 'Permite consultar el historial de ingresos y egresos de caja/banco',
  },
  {
    key: 'transactions:create',
    label: 'Registrar Transacciones',
    category: 'Finanzas y Transacciones',
    description: 'Permite crear nuevos ingresos o egresos de caja',
  },
  {
    key: 'transactions:update',
    label: 'Editar Transacciones',
    category: 'Finanzas y Transacciones',
    description: 'Permite modificar transactions previamente registradas',
  },
  {
    key: 'transactions:delete',
    label: 'Anular / Eliminar Transacciones',
    category: 'Finanzas y Transacciones',
    description: 'Permite eliminar o anular transactions (alta sensibilidad contable)',
  },
  {
    key: 'reports:view',
    label: 'Ver Reportes Financieros',
    category: 'Reportes',
    description: 'Acceso a dashboards de rendimiento, balances y cierre de caja',
  },
  {
    key: 'rates:sync',
    label: 'Sincronizar BCV Oficial (API)',
    category: 'Tasas de Cambio',
    description: 'Permite actualizar automáticamente la tasa del BCV con 1 clic sin ingresar números a mano',
  },
  {
    key: 'rates:manual_override',
    label: 'Ingreso Manual de Emergencia',
    category: 'Tasas de Cambio',
    description: 'Permite ingresar o modificar tasas numéricas a mano en la app (Seguro contra caídas del BCV)',
  },
  {
    key: 'clients:read',
    label: 'Ver Clientes',
    category: 'Clientes y Personal',
    description: 'Permite visualizar la lista e historia de customers',
  },
  {
    key: 'clients:manage',
    label: 'Gestionar Clientes',
    category: 'Clientes y Personal',
    description: 'Permite crear, editar o eliminar registros de customers',
  },
  {
    key: 'staff:read',
    label: 'Ver Personal',
    category: 'Clientes y Personal',
    description: 'Permite visualizar el equipo de trabajo y turnos',
  },
  {
    key: 'staff:manage',
    label: 'Gestionar Personal y Sueldos',
    category: 'Clientes y Personal',
    description: 'Permite modificar fichas de staff, sueldos y modalidades de pago',
  },
  {
    key: 'users:manage',
    label: 'Gestionar Usuarios del Sistema',
    category: 'Administración',
    description: 'Permite crear cuentas de acceso a la app y asignar sus permisos',
  },
  {
    key: 'settings:manage',
    label: 'Gestionar Configuración General',
    category: 'Administración',
    description: 'Permite editar branches, categorías de ingresos/egresos y métodos de pago',
  },
];

/**
 * Checks whether a user profile has a specific permission.
 * - System Owner (`is_owner: true`) has full access to ALL permissions (*).
 * - Other users have access ONLY if the permission key is explicitly present in `profile.permissions`.
 */
export function checkPermission(
  profile: Partial<UserProfileDocType> | null | undefined,
  permission: PermissionKey
): boolean {
  if (!profile) return false;

  // 1. Superuser / System Owner bypasses all checks
  if (profile.is_owner === true) return true;

  // 2. Explicit permission list check
  if (Array.isArray(profile.permissions)) {
    return profile.permissions.includes(permission);
  }

  return false;
}
